import axios from 'axios';
const API_BASE_URL = '/api';
const POLL_API_URL = `${API_BASE_URL}/polls`;

// For request cancellation
const controllers = {};

// Simple cache implementation
const cache = {
    polls: { data: null, timestamp: null },
    userVotes: { data: null, timestamp: null },
    pollDetails: {},
    maxAge: 5 * 60 * 1000, // 5 minutes cache validity

    isValid(key) {
        if (!this[key]?.timestamp) return false;
        return Date.now() - this[key].timestamp < this.maxAge;
    },

    set(key, data) {
        this[key] = { data, timestamp: Date.now() };
    },

    invalidate(key) {
        if (key === 'all') {
            this.polls = { data: null, timestamp: null };
            this.userVotes = { data: null, timestamp: null };
            this.pollDetails = {};
        } else if (key === 'pollDetails') {
            this.pollDetails = {};
        } else {
            this[key] = { data: null, timestamp: null };
        }
    }
};

// Error handler factory
const createErrorHandler = (operation) => (error) => {
    if (axios.isCancel(error)) {
        console.log(`Request for ${operation} was cancelled`);
        return Promise.reject({ cancelled: true, message: 'Request cancelled' });
    }

    // Extract the most useful error message
    const errorMessage = error.response?.data?.message || error.message || `Error during ${operation}`;

    console.error(`Error ${operation}:`, error);
    return Promise.reject({
        status: error.response?.status,
        message: errorMessage,
        operation,
        originalError: error
    });
};

export const pollService = {
    // Get all polls with optional pagination
    getAllPolls: async (page = 1, limit = 20, useCache = true) => {
        // Cancel previous request if exists
        if (controllers.getAllPolls) {
            controllers.getAllPolls.abort();
        }

        // Check cache first if enabled
        if (useCache && cache.isValid('polls')) {
            return cache.polls.data;
        }

        // Setup cancellation
        controllers.getAllPolls = new AbortController();

        try {
            const response = await axios.get(POLL_API_URL, {
                params: { page, limit },
                signal: controllers.getAllPolls.signal
            });

            // Cache the response
            cache.set('polls', response.data);

            return response.data;
        } catch (error) {
            throw createErrorHandler('fetching polls')(error);
        } finally {
            controllers.getAllPolls = null;
        }
    },

    // Create a new poll
    createPoll: async (pollData) => {
        // Invalidate cache on mutation
        cache.invalidate('polls');

        try {
            const response = await axios.post(POLL_API_URL, pollData);
            return response.data;
        } catch (error) {
            throw createErrorHandler('creating poll')(error);
        }
    },

    // Delete a poll
    deletePoll: async (pollId) => {
        // Invalidate cache on mutation
        cache.invalidate('polls');
        cache.invalidate('pollDetails');

        try {
            const response = await axios.delete(`${POLL_API_URL}/${pollId}`);
            return response.data;
        } catch (error) {
            throw createErrorHandler('deleting poll')(error);
        }
    },

    // Vote on a poll
    votePoll: async (pollId, optionId) => {
        // Invalidate cache on mutation
        cache.invalidate('polls');
        cache.invalidate('userVotes');
        if (cache.pollDetails[pollId]) {
            delete cache.pollDetails[pollId];
        }

        try {
            const response = await axios.post(`${POLL_API_URL}/${pollId}/vote`, { optionId });
            return response.data;
        } catch (error) {
            throw createErrorHandler('submitting vote')(error);
        }
    },

    // Get user votes (to check if user has already voted)
    getUserVotes: async (useCache = true) => {
        // Cancel previous request if exists
        if (controllers.getUserVotes) {
            controllers.getUserVotes.abort();
        }

        // Check cache first if enabled
        if (useCache && cache.isValid('userVotes')) {
            return cache.userVotes.data;
        }

        // Setup cancellation
        controllers.getUserVotes = new AbortController();

        try {
            const response = await axios.get(`${POLL_API_URL}/user-votes`, {
                signal: controllers.getUserVotes.signal
            });

            // Cache the response
            cache.set('userVotes', response.data);

            return response.data;
        } catch (error) {
            throw createErrorHandler('fetching user votes')(error);
        } finally {
            controllers.getUserVotes = null;
        }
    },

    // Get poll details
    getPollDetails: async (pollId, useCache = true) => {
        // Cancel previous request if exists
        if (controllers[`getPollDetails-${pollId}`]) {
            controllers[`getPollDetails-${pollId}`].abort();
        }

        // Check cache first if enabled
        if (useCache && cache.pollDetails[pollId]?.timestamp &&
            (Date.now() - cache.pollDetails[pollId].timestamp < cache.maxAge)) {
            return cache.pollDetails[pollId].data;
        }

        // Setup cancellation
        controllers[`getPollDetails-${pollId}`] = new AbortController();

        try {
            const response = await axios.get(`${POLL_API_URL}/${pollId}`, {
                signal: controllers[`getPollDetails-${pollId}`].signal
            });

            // Cache the response
            cache.pollDetails[pollId] = {
                data: response.data,
                timestamp: Date.now()
            };

            return response.data;
        } catch (error) {
            throw createErrorHandler(`fetching poll details for ID ${pollId}`)(error);
        } finally {
            controllers[`getPollDetails-${pollId}`] = null;
        }
    },

    // Update an existing poll
    updatePoll: async (pollId, pollData) => {
        // Invalidate cache on mutation
        cache.invalidate('polls');
        if (cache.pollDetails[pollId]) {
            delete cache.pollDetails[pollId];
        }

        try {
            const response = await axios.put(`${POLL_API_URL}/${pollId}`, pollData);
            return response.data;
        } catch (error) {
            throw createErrorHandler('updating poll')(error);
        }
    },

    // Close a poll (prevent further voting)
    closePoll: async (pollId) => {
        // Invalidate cache on mutation
        cache.invalidate('polls');
        if (cache.pollDetails[pollId]) {
            delete cache.pollDetails[pollId];
        }

        try {
            const response = await axios.patch(`${POLL_API_URL}/${pollId}/close`);
            return response.data;
        } catch (error) {
            throw createErrorHandler('closing poll')(error);
        }
    },

    // Force cache refresh
    invalidateCache: (key = 'all') => {
        cache.invalidate(key);
    }
};
